const getDefaultDataUser = () => ({
  token: '',
  empresa: '',
  nome_usuario: '',
  id_funcao: '0',
  nome_fantasia: '',
  site: '',
});

const MAX_HUMAN_SUPPORT_NOTIFICATION_COUNT = 9;

const persistEmpresas = (empresas) => {
  localStorage.setItem('empresas', JSON.stringify((empresas || []).map((empresa) => {
    const { atendimentos, atendimentosVisualizados, userContext, pendingRestart, ...dados } = empresa;
    return dados;
  })));
};

const persistEmpresaAtiva = (empresa) => {
  if (!empresa) {
    return;
  }

  const userToken = empresa.key || '';
  const empresaToken = empresa.token || '';

  localStorage.setItem('key', userToken);
  localStorage.setItem('token', empresaToken);
  localStorage.setItem('email', empresa.email || '');
  localStorage.setItem('title', `Bot - ${empresa.nome_fantasia}`);
  window.document.title = `Bot - ${empresa.nome_fantasia}`;
};

const normalizeEmpresa = (empresa, response, index) => {
  const dados = response.data.dados;
  const bandeiras = response.data.bandeiras || dados.bandeiras;
  const userToken = empresa.key || '';
  const empresaToken = response.data.empresa || dados.token || empresa.token || '';
  const xAuth = response.data.x_auth ? `Bearer ${response.data.x_auth}` : (empresa.x_auth || '');

  return {
    ...empresa,
    key: userToken,
    connectionId: empresa.connectionId || userToken,
    bandeiras: bandeiras ? bandeiras.map((b) => b.id_empresa) : null,
    nome_fantasia: dados.nome_fantasia,
    telefone: `55${dados.telefone}`,
    img_empresa: dados.url_imagem,
    id_usuario: dados.id_usuario,
    id_empresa: dados.id_empresa,
    nome_usuario: dados.nome,
    site: dados.site,
    email: dados.email,
    token: empresaToken,
    x_auth: xAuth,
    wpp: index + 1,
    partition: empresa.partition !== undefined ? empresa.partition : (index === 0 ? '' : `persist:whatsapp${dados.id_usuario}`),
    userContext: response.data,
    atendimentos: empresa.atendimentos || [],
  };
};

const normalizeEmpresasDefault = (empresas = []) => {
  if (!empresas.length) {
    return [];
  }

  const defaultIndex = empresas.findIndex((empresa) => empresa.isDefault);
  const selectedIndex = defaultIndex >= 0 ? defaultIndex : 0;

  return empresas.map((empresa, index) => ({
    ...empresa,
    isDefault: index === selectedIndex,
  }));
};

export const authState = {
  dataUser: getDefaultDataUser(),
  wpp_session: [],
  blockedConnections: [],
  canPlay: false,
  isReady: false,
  config: { version: null, pauseWpp: 2 },
  version: 0,
  wppVersion: 0,
  session: null,
  empresas: [],
};

const sameWppSession = (current, next) => {
  if (!current || !next) {
    return false;
  }

  return String(current.connectionId || current.key || current.wpp) === String(next.connectionId || next.key || next.wpp);
};

const normalizeWppSessions = (sessions = []) => {
  return sessions.reduce((acc, session) => {
    const index = acc.findIndex((current) => sameWppSession(current, session));

    if (index >= 0) {
      acc.splice(index, 1, {
        ...acc[index],
        ...session,
      });
      return acc;
    }

    acc.push(session);
    return acc;
  }, []);
};

const isSameEmpresa = (left, right) => String(left || '') === String(right || '');

const getEmpresaAtiva = (empresas = []) => {
  return empresas.find((empresa) => empresa.isDefault) || null;
};

const getAtendimentosCount = (empresa) => {
  const total = empresa && empresa.atendimentos
    ? empresa.atendimentos.length
    : 0;

  return total > MAX_HUMAN_SUPPORT_NOTIFICATION_COUNT
    ? MAX_HUMAN_SUPPORT_NOTIFICATION_COUNT
    : total;
};

const syncAtendimentosState = (state) => {
  const empresaAtiva = getEmpresaAtiva(state.empresas);
  const total = getAtendimentosCount(empresaAtiva);

  state.atendimentos = {
    status: total > 0,
    total,
  };

  const title = localStorage.getItem('title');

  if (title) {
    window.document.title = title + (total > 0 ? ` (${total})` : '');
  }
};

export const authMutations = {
  setDataUser: (state, data) => {
    const dados = data.dados;
    const master = parseInt(dados.master || '0', 10) > 0;

    state.dataUser = {
      ...dados,
      master,
      nivelMaster: Number(dados.master),
      admin: (dados.id_funcao <= '1'),
      isGarcom: (dados.id_funcao === '3'),
      entregadores: data.entregadores && data.entregadores.length ? data.entregadores : null,
      empresa: data.empresa || data.dados.token || state.dataUser.empresa || '',
    };
  },

  SET_EMPRESAS(state, empresas) {
    state.empresas = empresas || [];
    syncAtendimentosState(state);
  },
  SET_CAN_PLAY(state, canPlay) {
    state.canPlay = canPlay;
  },

  SET_IS_READY(state, isReady) {
    state.isReady = isReady;
  },

  SET_VERSION(state, version) {
    state.version = version;
  },

  SET_WPP_VERSION(state, version) {
    state.wppVersion = Number(version) || 0;
  },

  SET_CONNECTION_BLOCKED(state, { connectionId, blocked }) {
    state.blockedConnections = state.blockedConnections.filter(id => id !== connectionId);
    if (blocked) state.blockedConnections.push(connectionId);
  },

  SET_WPP_SESSION(state, sessions) {
    state.wpp_session = normalizeWppSessions(sessions);
  },

  ADD_WPP_SESSION(state, session) {
    const index = state.wpp_session.findIndex((current) => sameWppSession(current, session));

    if (index >= 0) {
      state.wpp_session.splice(index, 1, {
        ...state.wpp_session[index],
        ...session,
      });
      return;
    }

    state.wpp_session.push(session);
  },

  SET_SESSION(state, session) {
    state.session = session || null;
  },

  SET_CONFIG(state, config) {
    state.config = config || { version: null, pauseWpp: 2 };
  },

  SYNC_ATENDIMENTOS(state) {
    syncAtendimentosState(state);
  },
};

export const authActions = {
  hydrateEmpresas({ commit }, { parseJSONObject }) {
    sessionStorage.removeItem('atendimentosVisualizados');
    let empresas = parseJSONObject(localStorage.getItem('empresas'));
    const token = localStorage.getItem('key');

    if (!empresas && token) {
      empresas = [{ key: token, token: localStorage.getItem('token') || '', isDefault: true }];
    }

    empresas = (empresas || []).map((empresa) => {
      return {
        ...empresa,
        key: empresa.key || '',
        connectionId: empresa.connectionId || empresa.key || '',
        token: empresa.token || '',
        x_auth: empresa.x_auth || '',
        atendimentos: [],
      };
    });

    empresas = normalizeEmpresasDefault(empresas);

    commit('SET_EMPRESAS', empresas || []);
    return empresas || [];
  },

  async autenticarEmpresa({ commit, state }, { http, baseServer, index, authRequests }) {
    const empresa = state.empresas[index];

    if (!empresa) {
      return { success: false };
    }

    try {
      const request = () => http.post(`${baseServer}auth/v2/me`, { token: empresa.key });
      // Share only within this startup pass; each connection keeps its own partition and index.
      if (authRequests && !authRequests.has(empresa.key)) {
        authRequests.set(empresa.key, request());
      }
      const response = await (authRequests ? authRequests.get(empresa.key) : request());

      if (!response.data.success) {
        return { success: false };
      }

      const authenticatedEmpresa = normalizeEmpresa(empresa, response, index);
      const empresas = state.empresas.map((current, currentIndex) => (
        currentIndex === index ? authenticatedEmpresa : current
      ));
      const statusEmpresa = response.data.dados.status_empresa || '2';
      const habilitarLebot = response.data.dados.habilitar_lebot || '1';
      const deveAtivarContexto = authenticatedEmpresa.isDefault || statusEmpresa !== '2' || habilitarLebot !== '1';

      commit('SET_EMPRESAS', empresas);

      if (deveAtivarContexto) {
        persistEmpresaAtiva(authenticatedEmpresa);
        commit('setDataUser', response.data);
      }

      return {
        success: true,
        response,
        empresa: authenticatedEmpresa,
        statusEmpresa,
        habilitarLebot,
      };
    } catch (error) {
      if (error && error.status === 401) {
        const empresas = state.empresas.filter((current) => current.key !== empresa.key);
        commit('SET_EMPRESAS', empresas);
        persistEmpresas(empresas);

        return { success: false, unauthorized: true };
      }

      throw error;
    }
  },

  finalizeEmpresas({ commit, state }) {
    if (!state.empresas.length) {
      persistEmpresas([]);
      return [];
    }

    const empresas = normalizeEmpresasDefault(state.empresas);

    commit('SET_EMPRESAS', empresas);
    persistEmpresas(empresas);
    const ativa = getEmpresaAtiva(empresas);
    persistEmpresaAtiva(ativa);
    if (ativa && ativa.userContext) commit('setDataUser', ativa.userContext);

    return empresas;
  },

  addWhatsapp({ commit, state }, conta) {
    if (!conta || state.empresas.length >= 3) return false;
    const connectionId = window.crypto.randomUUID();
    const empresas = [...state.empresas, {
      ...conta,
      connectionId,
      partition: `persist:whatsapp-${connectionId}`,
      wpp: state.empresas.length + 1,
      isDefault: false,
      pendingRestart: true,
      atendimentos: [],
    }];
    commit('SET_EMPRESAS', empresas);
    persistEmpresas(empresas);
    return true;
  },

  addConta({ commit, state }, data) {
    if (state.empresas.length >= 3 || state.empresas.some(conta =>
      conta.key === data.token || String(conta.id_empresa) === String(data.id_empresa))) return false;
    const connectionId = window.crypto.randomUUID();
    const empresas = [...state.empresas, {
      key: data.token,
      connectionId,
      partition: `persist:whatsapp-${connectionId}`,
      id_empresa: data.id_empresa,
      nome_fantasia: data.nome_fantasia || 'Nova conta',
      email: data.email || '',
      token: '',
      isDefault: false,
      pendingRestart: true,
      atendimentos: [],
    }];
    commit('SET_EMPRESAS', empresas);
    persistEmpresas(empresas);
    return true;
  },

  removePendingConta({ commit, state }, conta) {
    if (!conta.pendingRestart) return;
    const empresas = state.empresas.filter(item => item.connectionId !== conta.connectionId);
    commit('SET_EMPRESAS', empresas);
    persistEmpresas(empresas);
  },

  selectEmpresa({ commit, state }, conta) {
    if (conta.pendingRestart) return;
    const empresas = state.empresas.map((empresa) => ({
      ...empresa,
      isDefault: empresa.connectionId === conta.connectionId,
    }));
    const empresaAtiva = empresas.find((empresa) => empresa.connectionId === conta.connectionId) || conta;
    const session = state.wpp_session.find((wpp) => (wpp.connectionId || wpp.key) === conta.connectionId) || null;

    commit('SET_EMPRESAS', empresas);
    commit('SET_SESSION', session);
    persistEmpresas(empresas);
    persistEmpresaAtiva(empresaAtiva);
    if (empresaAtiva.userContext) commit('setDataUser', empresaAtiva.userContext);
    commit('SYNC_ATENDIMENTOS');
  },

  setAtendimentosEmpresas({ commit, state }, atendimentos) {
    const empresas = state.empresas.map((empresa) => ({
      ...empresa,
      atendimentos: atendimentos.filter((item) => {
        const session = state.wpp_session.find((wpp) => (wpp.connectionId || wpp.key) === empresa.connectionId);
        const mesmaEmpresa = isSameEmpresa(item.id_empresa, empresa.id_empresa);
        return mesmaEmpresa && (!session
          ? state.empresas.filter((conta) => isSameEmpresa(conta.id_empresa, empresa.id_empresa)).length === 1
          : String(item.bot_telefone || '') === String(session.phone));
      }),
    }));

    commit('SET_EMPRESAS', empresas);
    persistEmpresas(empresas);
  },

  clearAtendimentosEmpresa({ commit, state }, key) {
    const empresas = state.empresas.map((empresa) => {
      if (empresa.connectionId !== key) {
        return empresa;
      }

      return {
        ...empresa,
        atendimentos: [],
      };
    });

    commit('SET_EMPRESAS', empresas);
    persistEmpresas(empresas);
  },

  removeAtendimentoEmpresa({ commit, state }, { idEmpresa, idChatbotUser }) {
    const empresas = state.empresas.map((empresa) => {
      if (!isSameEmpresa(empresa.id_empresa, idEmpresa)) {
        return empresa;
      }

      return {
        ...empresa,
        atendimentos: (empresa.atendimentos || [])
          .filter((item) => String(item.id_chatbot_user) !== String(idChatbotUser)),
      };
    });

    commit('SET_EMPRESAS', empresas);
    persistEmpresas(empresas);
  },

  restoreWppSessions({ commit, dispatch, state }) {
    const session = sessionStorage.getItem('wpp_session');
    const saved = session ? JSON.parse(session) || [] : [];
    const sessions = saved.filter(item => state.empresas.some(empresa => empresa.connectionId === (item.connectionId || item.key)))
      .map(item => ({ ...item, wpp: state.empresas.find(empresa => empresa.connectionId === (item.connectionId || item.key)).wpp }));

    commit('SET_WPP_SESSION', sessions);
    dispatch('syncSessionAtual');
    return sessions;
  },

  addWppSession({ commit, dispatch, state }, data) {
    commit('SET_CONNECTION_BLOCKED', {
      connectionId: data.connectionId || data.key,
      blocked: !!data.blocked,
    });
    if (data.blocked) {
      commit('SET_WPP_SESSION', state.wpp_session.filter(item => (item.connectionId || item.key) !== (data.connectionId || data.key)));
    } else {
      commit('ADD_WPP_SESSION', data);
    }
    sessionStorage.setItem('wpp_session', JSON.stringify(state.wpp_session));

    dispatch('syncSessionAtual');
  },

  syncSessionAtual({ commit, state }) {
    const empresaDefault = state.empresas.find((empresa) => empresa.isDefault);
    const session = state.wpp_session.find((wpp) => (wpp.connectionId || wpp.key) === (empresaDefault || {}).connectionId) || null;
    commit('SET_SESSION', session);
    return session;
  },
};
